import { Reducer } from "redux";

import { ClientContact, ClientContactCount } from "@/Contracts/Clients";
import { RecentOpportunityItemView } from "@/Contracts/Opportunity";
import { RecentDocumentItemView } from "@/Contracts/Documents";
import { ContactsActionTypes, ContactsActions } from "@/Actions/Contacts";
import {
	CustomView,
	CustomViewResponse,
	CustomViewsMetadata
} from "@/Contracts/UI";

export interface ContactsState {
	readonly isLoading: boolean;
	readonly contactList: ClientContact[];
	readonly contactCount: ClientContactCount;
	readonly currentActiveContact: ClientContact | null;
	readonly addContactDrawerOpen: boolean;
	readonly editContactDrawerOpen: boolean;
	readonly viewList: CustomViewResponse | null;
	readonly currentActiveView: CustomView | null;
	readonly viewMetadata: CustomViewsMetadata | null;
	readonly isViewChanged: boolean;
	readonly isBulkActionsRecordsSelected: boolean;
	readonly recentOpportunityByContactId: Record<
		string,
		RecentOpportunityItemView | null
	>;
	readonly recentDocumentByContactId: Record<
		string,
		RecentDocumentItemView | null
	>;
}

export const initialContactsState: ContactsState = {
	isLoading: false,
	contactList: [],
	contactCount: { all: 0 },
	currentActiveContact: null,
	addContactDrawerOpen: false,
	editContactDrawerOpen: false,
	viewList: null,
	currentActiveView: null,
	viewMetadata: null,
	isViewChanged: false,
	isBulkActionsRecordsSelected: false,
	recentOpportunityByContactId: {},
	recentDocumentByContactId: {}
};

export const ContactsReducer: Reducer<ContactsState, ContactsActions> = (
	state: ContactsState = initialContactsState,
	action: ContactsActions
): ContactsState => {
	switch (action.type) {
		case ContactsActionTypes.SHOW_CONTACTS_LOADER:
			return { ...state, isLoading: true };

		case ContactsActionTypes.HIDE_CONTACTS_LOADER:
			return { ...state, isLoading: false };

		case ContactsActionTypes.FETCH_CONTACTS_REQUESTED:
			return state;

		case ContactsActionTypes.FETCH_CONTACTS_SUCCESSFUL:
			return {
				...state,
				contactList: action.contactList.data,
				contactCount: action.contactList.filtered_count_map ?? {
					...state.contactCount,
					all:
						state.contactCount?.all ??
						action.contactList.data?.length ??
						0
				}
			};

		case ContactsActionTypes.FETCH_CONTACTS_FAILED:
			return state;

		case ContactsActionTypes.FETCH_ALL_CONTACT_VIEWS_REQUESTED:
			return state;

		case ContactsActionTypes.FETCH_ALL_CONTACT_VIEWS_SUCCESSFUL:
			return { ...state, viewList: action.viewList };

		case ContactsActionTypes.FETCH_ALL_CONTACT_VIEWS_FAILED:
			return state;

		case ContactsActionTypes.FETCH_CONTACT_VIEW_METADATA_REQUESTED:
			return state;

		case ContactsActionTypes.FETCH_CONTACT_VIEW_METADATA_SUCCESSFUL:
			return { ...state, viewMetadata: action.metadata };

		case ContactsActionTypes.FETCH_CONTACT_VIEW_METADATA_FAILED:
			return state;

		case ContactsActionTypes.CREATE_CONTACT_VIEW_SUCCESSFUL:
			return {
				...state,
				currentActiveView: action.view,
				viewList: state.viewList
					? {
							...state.viewList,
							data: [...(state.viewList?.data ?? []), action.view]
					  }
					: state.viewList,
				isViewChanged: false
			};

		case ContactsActionTypes.CREATE_CONTACT_VIEW_REQUESTED:
		case ContactsActionTypes.CREATE_CONTACT_VIEW_FAILED:
			return state;

		case ContactsActionTypes.UPDATE_CONTACT_VIEW_SUCCESSFUL:
			return {
				...state,
				currentActiveView: action.view,
				viewList: state.viewList
					? {
							...state.viewList,
							data: state.viewList?.data?.map(view =>
								view.id === action.view.id ? action.view : view
							)
					  }
					: state.viewList,
				isViewChanged: false
			};

		case ContactsActionTypes.UPDATE_CONTACT_VIEW_REQUESTED:
		case ContactsActionTypes.UPDATE_CONTACT_VIEW_FAILED:
			return state;

		case ContactsActionTypes.DELETE_CONTACT_VIEW_SUCCESSFUL:
			return {
				...state,
				currentActiveView: action.initialView,
				viewList: state.viewList
					? {
							...state.viewList,
							data: state.viewList?.data?.filter(
								view => view.id !== action.deletedView.id
							)
					  }
					: state.viewList,
				isViewChanged: false
			};

		case ContactsActionTypes.DELETE_CONTACT_VIEW_REQUESTED:
		case ContactsActionTypes.DELETE_CONTACT_VIEW_FAILED:
			return state;

		case ContactsActionTypes.UPDATE_CONTACTS_CURRENT_ACTIVE_VIEW:
			return {
				...state,
				currentActiveView: action.currentActiveView,
				isViewChanged: action.isViewChanged
			};

		case ContactsActionTypes.RESET_CONTACTS_CURRENT_ACTIVE_VIEW:
			return {
				...state,
				currentActiveView: null,
				isViewChanged: false,
				viewList: null,
				viewMetadata: null,
				recentOpportunityByContactId: {},
				recentDocumentByContactId: {}
			};

		case ContactsActionTypes.BULK_ACTION_RECORDS_SELECTED:
			return { ...state, isBulkActionsRecordsSelected: true };

		case ContactsActionTypes.BULK_ACTION_RECORDS_RESET:
			return { ...state, isBulkActionsRecordsSelected: false };

		case ContactsActionTypes.BULK_DELETE_RECORDS_SUCCESSFUL:
		case ContactsActionTypes.BULK_UPDATE_RECORDS_SUCCESSFUL:
			return { ...state, isBulkActionsRecordsSelected: false };

		case ContactsActionTypes.BULK_DELETE_RECORDS_REQUESTED:
		case ContactsActionTypes.BULK_DELETE_RECORDS_FAILED:
		case ContactsActionTypes.BULK_UPDATE_RECORDS_REQUESTED:
		case ContactsActionTypes.BULK_UPDATE_RECORDS_FAILED:
			return state;

		case ContactsActionTypes.ADD_CONTACT_DRAWER_OPENED:
			return {
				...state,
				addContactDrawerOpen: true,
				editContactDrawerOpen: false,
				currentActiveContact: null
			};

		case ContactsActionTypes.ADD_CONTACT_DRAWER_CLOSED:
			return { ...state, addContactDrawerOpen: false };

		case ContactsActionTypes.EDIT_CONTACT_DRAWER_OPENED:
			return {
				...state,
				addContactDrawerOpen: false,
				editContactDrawerOpen: true,
				currentActiveContact: action.contact
			};

		case ContactsActionTypes.EDIT_CONTACT_DRAWER_CLOSED:
			return {
				...state,
				editContactDrawerOpen: false,
				currentActiveContact: null
			};

		case ContactsActionTypes.MERGE_RECENT_OPPORTUNITY_FOR_CONTACTS:
			return {
				...state,
				recentOpportunityByContactId: {
					...state.recentOpportunityByContactId,
					...action.map
				}
			};

		case ContactsActionTypes.MERGE_RECENT_DOCUMENT_FOR_CONTACTS:
			return {
				...state,
				recentDocumentByContactId: {
					...state.recentDocumentByContactId,
					...action.map
				}
			};

		default:
			return state;
	}
};
